import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tag } from 'src/@vymo/ui/atoms';
import Button from 'src/@vymo/ui/atoms/button';
import DotsLoader from 'src/@vymo/ui/atoms/loader/indicator/dots';
import Text from 'src/@vymo/ui/atoms/text';
import { Card, SegmentedTabs } from 'src/@vymo/ui/blocks';
import { ReactComponent as AddIcon } from 'src/assets/icons/addBoxLine.svg';
import { ReactComponent as SuggestionIcon } from 'src/assets/icons/bulb.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/icons/calendar.svg';
import { ReactComponent as ViewAllIcon } from 'src/assets/icons/eyeOn.svg';
import { ReactComponent as NotificationIcon } from 'src/assets/icons/horn.svg';
import { ReactComponent as NoSuggestion } from 'src/assets/icons/noActivity.svg';
import { ReactComponent as NoNotificationIcon } from 'src/assets/icons/noNotification.svg';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { formatDateTimeToReadableEnglish, isCTAValidForWeb } from '../queries';
import {
  getNotificationFilters,
  getNotifications,
  getSuggestions,
} from '../selector';
import { fetchNudges } from '../thunk';
import { tagVariant } from './queries';
import styles from './index.module.scss';

function NotificationsAndSuggestions() {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<'suggestion' | 'notification'>(
    'suggestion',
  );

  const notifications = useAppSelector(getNotifications);
  const suggestions = useAppSelector(getSuggestions);
  const notificationFilters = useAppSelector(getNotificationFilters);

  const [filterConfig, setFilterConfig] = useState<Record<string, any>>({});
  const [visibleItemsCount, setVisibleItemsCount] = useState(5);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchNudges({ nudgeType: 'NOTIFICATION' }));
    dispatch(fetchNudges({ nudgeType: 'SUGGESTION' }));
  }, [dispatch]);

  useEffect(() => {
    if (notificationFilters?.length) {
      const config: Record<string, any> = {};
      notificationFilters.forEach((filter) => {
        config[filter.code] = 'all';
      });
      setFilterConfig(config);
    }
  }, [notificationFilters]);

  useEffect(() => {
    setVisibleItemsCount(5);
  }, [activeTab, filterConfig]);

  const icon = useCallback((iconType: string) => {
    switch (iconType) {
      case 'Calendar':
        return <CalendarIcon />;
      case 'View All':
        return <ViewAllIcon />;
      case 'Schedule Activity':
        return <AddIcon />;
      default:
        return '';
    }
  }, []);

  const dataToShow = useMemo(() => {
    if (activeTab === 'suggestion') return suggestions;

    return notifications.filter((n) => {
      const cls = n.classification || 'notification';

      const activeFilter = filterConfig[cls];

      return (
        activeFilter === 'all' || activeFilter === cls || !n.classification
      );
    });
  }, [activeTab, suggestions, notifications, filterConfig]);

  const visibleData = useMemo(
    () => (dataToShow || [])?.slice(0, visibleItemsCount),
    [dataToShow, visibleItemsCount],
  );

  const applyFiltering = useCallback((code: string, value: string) => {
    setFilterConfig((prev) => ({ ...prev, [code]: value }));
  }, []);

  const handleScroll = useCallback(() => {
    if (!listWrapperRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listWrapperRef.current;

    if (scrollHeight - scrollTop - clientHeight < 100) {
      setVisibleItemsCount((prev) => {
        const newCount = prev + 5;
        return Math.min(newCount, dataToShow?.length);
      });
    }
  }, [dataToShow?.length]);

  useEffect(() => {
    const listElement = listWrapperRef.current;
    if (!listElement) return undefined;

    listElement.addEventListener('scroll', handleScroll);
    return () => {
      listElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className={styles.notificationDropdown}>
      <SegmentedTabs
        tabs={[
          {
            key: 'suggestion',
            label: 'Suggestions',
            iconProps: { icon: <SuggestionIcon />, position: 'left' },
          },
          {
            key: 'notification',
            label: 'Notifications',
            iconProps: { icon: <NotificationIcon />, position: 'left' },
          },
        ]}
        activeTabKey={activeTab}
        onTabChange={(key) => setActiveTab(key as any)}
        tabItemClassName={styles.notificationDropdown__toggle}
      />

      <div className={styles.notificationDropdown__notificationWrapper}>
        {notificationFilters?.length > 0 && activeTab === 'notification' && (
          <div className={styles.filtersWrapper}>
            {notificationFilters.map((filter) => (
              <div
                key={filter.code}
                className={styles.notificationDropdown__filter}
              >
                {filter.code_name_spinner_options?.map((option) => {
                  const isActive = filterConfig[filter.code] === option.code;
                  return (
                    <Button
                      key={option.code}
                      size="small"
                      type="text"
                      variant={isActive ? 'subtle' : 'default'}
                      className={styles.notificationDropdown__filter__option}
                      onClick={() => applyFiltering(filter.code, option.code)}
                    >
                      {option.name}
                    </Button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {Boolean(visibleData?.length) && (
          <div
            className={styles.notificationDropdown__list}
            ref={listWrapperRef}
          >
            {visibleData.map((item) => (
              <Card classNames={styles.notificationDropdown__list__item}>
                <div className={styles.notificationDropdown__list__item__text}>
                  <div
                    className={styles.notificationDropdown__list__item__title}
                  >
                    {item.body.title && (
                      <Text type="h6">{item.body.title}</Text>
                    )}
                    {item?.body?.tag && (
                      <Tag
                        className={
                          styles.notificationDropdown__list__item__descBox__title__tag
                        }
                        label={item.body.tag}
                        variant={tagVariant(item.body.tag)}
                      />
                    )}
                  </div>
                  <p
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: item.body?.message ?? '',
                    }}
                    className={styles.notificationDropdown__list__item__body}
                  />
                </div>
                <div
                  className={styles.notificationDropdown__list__item__ctaBox}
                >
                  {item.ctas.map(
                    (cta) =>
                      isCTAValidForWeb(cta) && (
                        <Button
                          key={cta.text}
                          type="primary"
                          variant="subtle"
                          iconProps={{
                            icon: icon(cta.text),
                            iconPosition: 'left',
                          }}
                        >
                          {cta.text}
                        </Button>
                      ),
                  )}

                  {item?.date && (
                    <Text
                      classNames={
                        styles.notificationDropdown__list__item__ctaBox__date
                      }
                    >
                      {formatDateTimeToReadableEnglish(item.date)}
                    </Text>
                  )}
                </div>
              </Card>
            ))}
            {Boolean(
              visibleData?.length && visibleData?.length < dataToShow?.length,
            ) && <DotsLoader />}
          </div>
        )}
      </div>
      {!dataToShow?.length && (
        <div className={styles.notificationDropdown__emptyState}>
          {activeTab === 'notification' ? (
            <>
              <NoNotificationIcon />
              <Text semiBold>No Notifications to show!</Text>
              <Text type="subText">
                You don&apos;t have any notifications to act on right now.
              </Text>
            </>
          ) : (
            <>
              <NoSuggestion />
              <Text semiBold>No Suggestions to show!</Text>
              <Text type="subText">
                You don&apos;t have any suggestions right now.
              </Text>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationsAndSuggestions;
